import { useState, useEffect } from "react"
import { BASE_URL } from "../apiConfig"
import { useAuth } from "../context/AuthProvider";
import { useLanguage } from "../context/LanguageProvider";
import { X } from "lucide-react"

export default function AnnonceDashboard() {
  const { t } = useLanguage();
  const [annonces, setAnnonces] = useState([])
  const [error, setError] = useState(null)
  const [visibleAnnonces, setVisibleAnnonces] = useState({})
  const [removedAnnonces, setRemovedAnnonces] = useState(new Set())
  const { token } = useAuth();


  const fetchAnnonces = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/getannonce`, {
        headers: {
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      })

      if (!res.ok) {
        const err = await res.json()
        setError(err.message || t('errors.no_active_announcements'))
        return
      }

      const data = await res.json()
      const annoncesArray = Array.isArray(data.annonce) ? data.annonce : [data.annonce].filter(Boolean)
      setAnnonces(annoncesArray)
      
      const initialVisibility = {}
      annoncesArray.forEach((annonce, index) => {
        initialVisibility[index] = false
      })
      setVisibleAnnonces(initialVisibility)
    } catch (err) {
      setError(t('errors.fetching_announcements'))
    }
  }

  useEffect(() => {
    fetchAnnonces()
  }, [])

  useEffect(() => {
    if (annonces.length > 0) {
      const timers = annonces.map((_, index) => {
        if (!removedAnnonces.has(index)) {
          return setTimeout(() => {
            setVisibleAnnonces(prev => ({
              ...prev,
              [index]: true
            }))
          }, 100 + (index * 150))
        }
        return null
      }).filter(Boolean)

      return () => timers.forEach(timer => clearTimeout(timer))
    }
  }, [annonces, removedAnnonces])

  const handleClose = (index) => {
    setVisibleAnnonces(prev => ({
      ...prev,
      [index]: false
    }))
    
    setTimeout(() => {
      setRemovedAnnonces(prev => new Set([...prev, index]))
    }, 300)
  }

  const activeAnnonces = annonces.filter((_, index) => !removedAnnonces.has(index))

  if (error || activeAnnonces.length === 0) {
    return null
  }

  return (
    <div className="mx-auto px-4 pt-5 space-y-2">
      {activeAnnonces.map((annonce, originalIndex) => {
        const actualIndex = annonces.indexOf(annonce)
        return (
          <div
                key={annonce.id || actualIndex}
                className={`relative overflow-hidden rounded-xl shadow-md transition-all duration-500 
                ${visibleAnnonces[actualIndex]
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-4"}`
                }
                style={{
                  background: `linear-gradient(135deg, ${annonce.color} 0%, ${annonce.color}90 100%)`,
                  backdropFilter: "blur(10px)",
                }}
              >
            <button
              onClick={() => handleClose(actualIndex)}
              className="absolute top-2 right-2 z-20 p-1 rounded-full hover:bg-white/30 transition"
              style={{
                background: `${annonce.color}60`,
                backdropFilter: "blur(4px)",
              }}
            >
              <X className="w-3 h-3" style={{ color: "#fff" }} />
            </button>

            <div className="relative z-10 max-lg:px-2 max-lg:mt-4 px-6 py-3 mt-2 md:px-8 text-center">
              <p className="font-semibold text-xs max-lg:text-[10px] leading-relaxed text-white">
                {annonce.annonce}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
